import { ChartIconAnimated } from "@cmp-src/icons/animated-nav-icons/ChartIconAnimated";
import { CheckInIconAnimated } from "@cmp-src/icons/animated-nav-icons/CheckInIconAnimated";
import { ClipboardCheckIconAnimated } from "@cmp-src/icons/animated-nav-icons/ClipboardCheckIconAnimated";
import { DashboardIconAnimated } from "@cmp-src/icons/animated-nav-icons/DashboardIconAnimated";
import { MapIconAnimated } from "@cmp-src/icons/animated-nav-icons/MapIconAnimated";
import { SidebarChartAnimated } from "@cmp-src/icons/animated-nav-icons/SidebarChartAnimated";
import { UserIconAnimated } from "@cmp-src/icons/animated-nav-icons/UserIconAnimated";
import { OrganizationSettings, Permissions } from "@gettactic/api";
import { MARIN_ROUTES } from "@gettactic/defines/src/web-routes";
import {
  isVisitorManagementEnabled,
  isWorkplaceRequestsEnabled
} from "@gettactic/helpers/src/featureFlags";
import { Building2, TrendingUp, UtensilsCrossed } from "lucide-react";
import { canAccessAnalytics } from "@/features/reporting/lib/access";
import { LayoutMenuItems } from "@/providers/LayoutProvider/types";

const ICON_SIZE = 23;

export const DEFAULT_MAIN_MENU_ITEMS: LayoutMenuItems = [
  {
    id: "dashboard",
    title: "Dashboard",
    url: MARIN_ROUTES.dashboard,
    icon: (props: { isActive?: boolean }) => (
      <DashboardIconAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    enabled: (
      permissions: Permissions,
      featureFlags: NonNullable<OrganizationSettings["feature_flags"]>
    ) => featureFlags.dashboard
  },
  {
    id: "map",
    title: "Map",
    url: MARIN_ROUTES.map,
    icon: (props: { isActive?: boolean }) => (
      <MapIconAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    enabled: (
      permissions: Permissions,
      featureFlags: NonNullable<OrganizationSettings["feature_flags"]>
    ) => featureFlags.map,
    appendFloorId: true
  },
  {
    id: "timeline",
    title: "Timeline",
    url: MARIN_ROUTES.timeline,
    icon: (props: { isActive?: boolean }) => (
      <SidebarChartAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    enabled: (
      permissions: Permissions,
      featureFlags: NonNullable<OrganizationSettings["feature_flags"]>
    ) => featureFlags.timeline,
    extraOnMobile: true
  },
  {
    id: "directory",
    title: "Directory",
    url: MARIN_ROUTES.directory,
    icon: (props: { isActive?: boolean }) => (
      <UserIconAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    enabled: true
  },
  {
    id: "catering",
    title: "Catering",
    url: "/catering",
    icon: () => <UtensilsCrossed width={ICON_SIZE} height={ICON_SIZE} />,
    enabled: true,
    orgSlugs: ["moodys", "tactic"]
  },
  // A space planning demo embedded for one customer; the page itself checks
  // the organization. "/space" because "/space-planning" is the real thing.
  {
    id: "space",
    title: "Space Planning",
    url: "/space",
    icon: () => <Building2 width={ICON_SIZE} height={ICON_SIZE} />,
    enabled: true,
    orgSlugs: ["advanceauto"]
  },
  {
    id: "visitors",
    title: "Visitors",
    url: MARIN_ROUTES.visitors,
    icon: (props: { isActive?: boolean }) => (
      <CheckInIconAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    enabled: (
      permissions: Permissions,
      featureFlags: NonNullable<OrganizationSettings["feature_flags"]>
    ) => isVisitorManagementEnabled(featureFlags.visitor_management),
    extraOnMobile: true
  },
  {
    id: "workplace-requests",
    title: "Workplace Requests",
    url: MARIN_ROUTES.workplaceRequests,
    icon: (props: { isActive?: boolean }) => (
      <ClipboardCheckIconAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    enabled: (
      permissions: Permissions,
      featureFlags: NonNullable<OrganizationSettings["feature_flags"]>
    ) =>
      isWorkplaceRequestsEnabled(featureFlags.workplace_requests) ||
      // Feature off: admins keep this entry so they can still reach the
      // room-reservations approval flow the queue collapses to. Everyone
      // else loses the item entirely.
      permissions.schedule_others ||
      permissions.schedule_others_managed,
    extraOnMobile: true
  },
  // Native reporting lives next to the Metabase reports while the two are
  // compared; it shows only with the reporting flag on, to owners and
  // report managers.
  {
    id: "analytics",
    title: "Analytics",
    url: MARIN_ROUTES.analytics,
    icon: () => <TrendingUp width={ICON_SIZE} height={ICON_SIZE} />,
    isNew: true,
    extraOnMobile: true,
    enabled: (_permissions, featureFlags, _userRoles, authenticatedUser) =>
      featureFlags.reporting === true && canAccessAnalytics(authenticatedUser)
  },
  // Reports must always be last
  {
    id: "reports",
    title: "Reports",
    url: MARIN_ROUTES.reports,
    icon: (props: { isActive?: boolean }) => (
      <ChartIconAnimated width={ICON_SIZE} height={ICON_SIZE} />
    ),
    extraOnMobile: true,
    enabled: (_permissions, _featureFlags, userRoles) =>
      userRoles.includes("owner") || userRoles.includes("report_manager")
  }
];
